import type {
  AssignablePlatform,
  CompanyAssignments,
  InvestmentPayment,
} from "../store/apis/companyApi";

/**
 * Plataformas a las que se puede asignar un responsable, en el orden en que se
 * enseñan. Son las operativas (las de los IDs de integración de la empresa); CTV no
 * está porque es un agregado de DV360 + Amazon y el backend la rechaza.
 */
export const ASSIGNABLE_PLATFORMS: {
  platform: AssignablePlatform;
  label: string;
}[] = [
  { platform: "DV360", label: "DV360" },
  { platform: "AMAZON_DSP", label: "Amazon DSP" },
  { platform: "META", label: "Meta" },
  { platform: "TIKTOK", label: "TikTok" },
  { platform: "GOOGLE_ADS", label: "Google Ads" },
  { platform: "REDDIT", label: "Reddit" },
  { platform: "SPOTIFY_ADS", label: "Spotify" },
  { platform: "HIVESTACK", label: "Hivestack" },
  { platform: "NATADS", label: "NatAds (Outbrain / Taboola)" },
  { platform: "BEAPOLO", label: "BeApolo (offline y otros)" },
];

interface PersonLike {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

/** Nombre y apellidos, o el email si no tiene nombre. */
export const personLabel = (person: PersonLike): string =>
  [person.firstName, person.lastName].filter(Boolean).join(" ") || person.email;

interface UserLike extends PersonLike {
  id: string;
  role?: string | null;
  active?: boolean | null;
}

/**
 * Las personas que pueden llevar una empresa: internas y activas. Un usuario CLIENT
 * no puede, y el backend lo rechazaría.
 */
export const assignableUsers = <T extends UserLike>(users: T[]): T[] =>
  users
    .filter((user) => user.active !== false && user.role !== "CLIENT")
    .sort((a, b) => personLabel(a).localeCompare(personLabel(b), "es"));

/** Quién puede pagar la inversión de una plataforma, en el orden en que se enseña. */
export const INVESTMENT_PAYERS: InvestmentPayment[] = [
  "TI_MEDIA_GROUP_SL",
  "CLIENTE",
];

/**
 * Quién paga por defecto en la empresa la plataforma operativa de una línea, o null si
 * la empresa no lo tiene configurado o la plataforma no se opera (X, LinkedIn...).
 */
export const defaultInvestmentPayment = (
  assignments: Pick<CompanyAssignments, "platformPayments"> | undefined,
  operatingPlatform: AssignablePlatform | null | undefined
): InvestmentPayment | null =>
  (operatingPlatform &&
    (assignments?.platformPayments ?? []).find(
      (payment) => payment.platform === operatingPlatform
    )?.investmentPayment) ||
  null;
